import { useCallback, useEffect, useRef, useState } from "react";
import { useBrand } from "@/lib/brand";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Trash2, Wallet, RefreshCcw, FileDown, Mail, Smartphone, Unlock, Lock, Pencil } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { eur, fecha } from "@/lib/format";
import { importeTotal, restoTrasSenal } from "@/lib/reserva";
import { estadoFianza, estadoReserva } from "@/lib/estados";
import { nivelCombustible } from "@/lib/equipment";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import HandoverForm from "@/components/HandoverForm";
import ReturnSettlement from "@/components/ReturnSettlement";
import ExpenseSheet from "@/components/ExpenseSheet";
import AdminCheckoutForm from "@/components/AdminCheckoutForm";
import ReservationProgress from "@/components/ReservationProgress";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ScheduleEditor from "@/components/ScheduleEditor";
import RegistryPanel from "@/components/registry/RegistryPanel";
import PrivacyActions from "@/components/PrivacyActions";
import { TEXTO_BORRADO, resumenBorrado } from "@/lib/privacidad";
import { useAuth } from "@/lib/auth";
import { esAdmin } from "@/lib/roles";

// De dónde viene el consentimiento de WhatsApp (`whatsapp_opt_in_source`): antes de la Tarea 14, el alta del panel
// lo daba con la casilla premarcada («admin»).
const ORIGEN_WHATSAPP = { checkin: " en el pre-checkin", admin: " desde el panel" };

function PhotoGrid({ photos }) {
  if (!photos?.length) return <div className="text-sm text-[#8A8A8A]">Sin fotos</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {photos.map((p, i) => (
        <a key={i} href={p} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-[#DCD8D0] bg-white">
          <img src={p} alt={`foto-${i}`} className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-[#8A8A8A] mb-1">{label}</div>
      <div className="text-sm text-[#1A1A1A]">{children}</div>
    </div>
  );
}

export default function ReservationDetail() {
  const brand = useBrand();
  const { id } = useParams();
  const navigate = useNavigate();
  // Anular, borrar, devolver la fianza, suprimir fotos y exportar son del administrador (backend/roles.py).
  const admin = esAdmin(useAuth()?.user);
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  // null = no se está editando; texto = borrador del email del cliente
  const [emailDraft, setEmailDraft] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState(null);
  // Motivo por el que no se puede borrar (409: se comunicó al registro de viajeros y se conserva 3 años).
  const [bloqueo, setBloqueo] = useState(null);
  // Tras retirar el consentimiento de WhatsApp, el botón desaparece: el foco pasa al texto del estado.
  const estadoWhatsapp = useRef(null);
  const [enfocarWhatsapp, setEnfocarWhatsapp] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations/${id}`);
      setR(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const onRefund = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/refund`);
      if (data.deduplicated) {
        toast.info("La fianza ya estaba liberada o devuelta");
      } else if (data.type === "release") {
        toast.success("Preautorización liberada (sin comisión). El cliente recibirá un email.");
      } else {
        toast.success(`Fianza devuelta (${eur(data.amount)}). El cliente recibirá un email.`);
      }
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSaveEmail = async (value) => {
    try {
      const { data } = await api.patch(`/reservations/${id}/client-email`, { client_email: value.trim() });
      setR(data);
      setEmailDraft(null);
      toast.success("Email del cliente actualizado. Los próximos envíos irán a esta dirección.");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // `aviso`: lo que se dice al guardar. El borrador del teléfono solo se cierra cuando lo que se guarda es el teléfono.
  const onSaveContact = async (changes, aviso = "Datos de contacto actualizados") => {
    try {
      const { data } = await api.patch(`/reservations/${id}/contact`, changes);
      setR(data);
      if ("client_phone" in changes) setPhoneDraft(null);
      toast.success(aviso);
      return true;
    } catch (err) {
      toast.error(formatError(err));
      return false;
    }
  };

  const onWithdrawWhatsapp = async () => {
    if (await onSaveContact({ whatsapp_opt_in: false }, "Avisos por WhatsApp retirados")) setEnfocarWhatsapp(true);
  };
  useEffect(() => {
    if (!enfocarWhatsapp) return;
    estadoWhatsapp.current?.focus();
    setEnfocarWhatsapp(false);
  }, [enfocarWhatsapp]);

  const onToggleSelfService = async (enabled) => {
    try {
      await api.patch(`/reservations/${id}/self-service`, { enabled });
      toast.success(enabled ? "Recogida autónoma activada" : "Recogida autónoma desactivada");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onConfirmSelfService = async (action) => {
    try {
      await api.post(`/reservations/${id}/self-service/confirm-${action}`);
      toast.success(action === "unlock" ? "Apertura confirmada — el cliente ya puede acceder" : "Cierre confirmado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onSendBalanceEmail = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/send-balance-email`);
      toast.success(data.status === "sent" ? "Aviso de pago enviado al cliente" : "No se pudo enviar el aviso");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onChargeRental = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/charge-rental`);
      toast.success(
        `Alquiler cobrado: ${eur(data.amount)} en la tarjeta guardada. El cliente recibirá un recibo por email.`
      );
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSendContract = async () => {
    try {
      await api.post(`/reservations/${id}/send-contract`);
      toast.success("Contrato enviado a cliente y propietario");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onDownloadContract = async () => {
    try {
      const resp = await api.get(`/reservations/${id}/contract.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-${r.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Axios serializes the error response as a Blob when responseType is blob,
      // so we read it back to get the FastAPI detail message
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          toast.error(JSON.parse(text).detail || "No se pudo generar el PDF");
        } catch {
          toast.error("No se pudo generar el PDF");
        }
      } else {
        toast.error(formatError(err));
      }
    }
  };

  const onDelete = async () => {
    try {
      const { data } = await api.delete(`/reservations/${id}`);
      toast.success(resumenBorrado(data));
      navigate("/admin");
    } catch (err) {
      if (err?.response?.status === 409) setBloqueo(formatError(err));
      else toast.error(formatError(err));
    }
  };

  // Anular no borra: la reserva se conserva como anulada y, si se comunicó al registro de viajeros, se comunica la
  // anulación (especificación §7.5).
  const onCancel = async () => {
    try {
      await api.post(`/reservations/${id}/cancel`);
      toast.success("Reserva anulada");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // Se anula una reserva confirmada sin entregar y sin la fianza viva (retenida o cobrada), como pide `/cancel`, y solo
  // el administrador: la anulación se comunica al registro de viajeros.
  // El motivo del 409 al borrar acaba con «Puedes anularla.»: solo si de verdad se puede, y sin repetirlo; con la
  // fianza viva, qué hacer antes.
  const fianzaViva = ["on_hold", "paid"].includes(r?.payment_status);
  const puedeAnular = admin && r?.status === "pending" && !fianzaViva;
  const SUGERENCIA = /\s*Puedes anularla\.?\s*$/;
  let motivoBloqueo = bloqueo;
  if (bloqueo && !puedeAnular) {
    motivoBloqueo = bloqueo.replace(SUGERENCIA, "");
    if (SUGERENCIA.test(bloqueo) && r?.status === "pending") {
      motivoBloqueo += " Para anularla, libera o devuelve antes la fianza.";
    }
  }

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/reserva/${r.code}`);
    toast.success("Enlace copiado");
  };

  if (!r)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">
        Cargando...
      </div>
    );

  const ci = r.checkin_data;
  const co = r.checkout_data;
  const rentalAmount = Number(r.total_amount || 0);
  const rentalStatus = r.rental_payment_status;
  // Con la señal cobrada, lo que queda por cobrar del alquiler es el resto.
  const signalPaid = r.signal_payment_status === "paid" ? Number(r.signal_amount || 0) : 0;
  const rentalDue = Math.max(0, rentalAmount - signalPaid);
  const rentalChargeable =
    rentalAmount > 0 && (rentalStatus === "pending" || rentalStatus === "external");
  const hasSavedCard = Boolean(r.stripe_payment_method_id);
  const RENTAL_STATUS_LABELS = {
    pending: "Pendiente de cobro",
    paid: "Cobrado",
    external: "Cobrado fuera de la plataforma",
    not_applicable: "—",
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] pb-12">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={copyLink} className="border-[#DCD8D0]" data-testid="detail-copy-link">
              <Copy className="h-4 w-4 mr-2" /> Copiar enlace
            </Button>
            {puedeAnular && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-[#DCD8D0]" data-testid="detail-cancel-btn">Anular reserva</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Anular la reserva?</AlertDialogTitle>
                    <AlertDialogDescription>
                      La reserva no se borra: se conserva como anulada y las noches quedan libres. Si ya se comunicó al
                      registro de viajeros, la anulación queda pendiente de hacerse a mano en los Mossos o en SES (la verás
                      en la tarjeta «Registro de viajeros») y la reserva se conserva 3 años. Lo que ya se haya cobrado
                      (la señal, el alquiler o los extras) no se devuelve solo: si corresponde, devuélvelo aparte.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Volver</AlertDialogCancel>
                    <AlertDialogAction onClick={onCancel} className="bg-marca hover:bg-marca-hover" data-testid="detail-cancel-confirm">
                      Anular
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {admin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="text-[#B54434]" aria-label="Eliminar la reserva" data-testid="detail-delete-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar reserva?</AlertDialogTitle>
                    <AlertDialogDescription>{TEXTO_BORRADO}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-[#B54434] hover:bg-[#9C3A2C]" data-testid="detail-delete-confirm">
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <AlertDialog open={Boolean(bloqueo)} onOpenChange={(abierto) => { if (!abierto) setBloqueo(null); }}>
              <AlertDialogContent data-testid="detail-delete-blocked">
                <AlertDialogHeader>
                  <AlertDialogTitle>No se puede eliminar</AlertDialogTitle>
                  <AlertDialogDescription>{motivoBloqueo}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cerrar</AlertDialogCancel>
                  {puedeAnular && (
                    <AlertDialogAction onClick={onCancel} className="bg-marca hover:bg-marca-hover" data-testid="detail-delete-blocked-cancel">
                      Anular la reserva
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-overline">Reserva</span>
            <div className="flex items-center gap-3 mt-2">
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">
                {r.client_name}
              </h1>
              <span className="font-mono text-sm font-semibold text-marca bg-[#EAE7E0] px-2.5 py-1 rounded-md">
                {r.code}
              </span>
            </div>
            <div className="text-sm text-[#1A1A1A] mt-1 flex flex-wrap items-center gap-2" data-testid="detail-vehicle">
              <span className="font-medium">{r.vehicle?.name || "Sin vehículo"}</span>
              {r.vehicle?.plate && <span className="font-mono text-xs text-[#5C5C5C]">{r.vehicle.plate}</span>}
              <ScheduleEditor reservation={r} onSaved={load} />
            </div>
            {emailDraft === null ? (
              <div className="text-sm text-[#5C5C5C] mt-1 flex items-center gap-1.5">
                <span data-testid="detail-client-email">{r.client_email}</span>
                <button
                  type="button"
                  onClick={() => setEmailDraft(r.client_email || "")}
                  className="text-[#8A8A8A] hover:text-marca"
                  title="Cambiar el email del cliente"
                  aria-label="Cambiar el email del cliente"
                  data-testid="detail-edit-email-btn"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <span>·</span>
                <span data-testid="detail-client-phone">{r.client_phone}</span>
                <button
                  type="button"
                  onClick={() => setPhoneDraft(r.client_phone || "")}
                  className="text-[#8A8A8A] hover:text-marca"
                  title="Cambiar el teléfono del cliente"
                  aria-label="Cambiar el teléfono del cliente"
                  data-testid="detail-edit-phone-btn"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <form
                className="mt-2 flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSaveEmail(emailDraft);
                }}
              >
                <Input
                  type="email"
                  required
                  autoFocus
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  className="h-9 w-72 bg-white"
                  data-testid="detail-email-input"
                />
                <Button type="submit" size="sm" className="bg-marca hover:bg-marca-hover" data-testid="detail-email-save">
                  Guardar
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setEmailDraft(null)}>
                  Cancelar
                </Button>
              </form>
            )}
            {r.checkin_data?.email && r.checkin_data.email.toLowerCase() !== (r.client_email || "").toLowerCase() && (
              <div className="text-xs text-[#9C6512] mt-1" data-testid="detail-checkin-email-hint">
                En el pre-checkin el cliente indicó <strong>{r.checkin_data.email}</strong>.{" "}
                <button type="button" className="underline" onClick={() => onSaveEmail(r.checkin_data.email)}>
                  Usar este email
                </button>
              </div>
            )}
            {phoneDraft !== null && (
              <form
                className="mt-2 flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSaveContact({ client_phone: phoneDraft });
                }}
              >
                <Input
                  type="tel"
                  required
                  autoFocus
                  value={phoneDraft}
                  onChange={(e) => setPhoneDraft(e.target.value)}
                  className="h-9 w-56 bg-white"
                  data-testid="detail-phone-input"
                />
                <Button type="submit" size="sm" className="bg-marca hover:bg-marca-hover" data-testid="detail-phone-save">
                  Guardar
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setPhoneDraft(null)}>
                  Cancelar
                </Button>
              </form>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C5C5C]">
              {/* El consentimiento lo da el cliente en el pre-checkin; desde el panel solo se retira. */}
              {r.whatsapp_opt_in ? (
                <span ref={estadoWhatsapp} tabIndex={-1} className="flex items-center gap-2" data-testid="detail-whatsapp-optin">
                  Avisos por WhatsApp: aceptados{ORIGEN_WHATSAPP[r.whatsapp_opt_in_source] || ""}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button type="button" className="underline hover:text-marca" aria-label="Retirar el consentimiento para los avisos por WhatsApp"
                              data-testid="detail-whatsapp-withdraw">
                        Retirar
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Retirar los avisos por WhatsApp?</AlertDialogTitle>
                        <AlertDialogDescription>
                          No se enviarán más avisos por WhatsApp de esta reserva, y desde el panel no se pueden volver a dar.
                          {r.checkin_data
                            ? " El cliente ya hizo el pre-checkin: en esta reserva no se podrán volver a activar."
                            : " Solo los puede aceptar de nuevo el cliente, al hacer el pre-checkin."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={onWithdrawWhatsapp} className="bg-marca hover:bg-marca-hover" data-testid="detail-whatsapp-withdraw-confirm">
                          Retirar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </span>
              ) : (
                <span ref={estadoWhatsapp} tabIndex={-1} data-testid="detail-whatsapp-optin">
                  {r.checkin_data
                    ? "Avisos por WhatsApp: no (retirado o no aceptado; no se puede volver a dar desde el panel)"
                    : "Avisos por WhatsApp: no (el cliente puede aceptarlos en el pre-checkin)"}
                </span>
              )}
              <label className="flex items-center gap-2">
                Idioma
                <select
                  value={r.locale || "es"}
                  onChange={(e) => onSaveContact({ locale: e.target.value })}
                  className="h-7 rounded border border-[#DCD8D0] bg-white px-1"
                  data-testid="detail-locale"
                >
                  <option value="es">Castellano</option>
                  <option value="ca">Català</option>
                </select>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-marca/10 text-marca border-0 hover:bg-marca/10" data-testid="detail-status-badge">{estadoReserva(r.status)}</Badge>
            <Badge className="bg-acento/15 text-acento border-0 hover:bg-acento/15" data-testid="detail-deposit-badge">Fianza: {estadoFianza(r.payment_status)}</Badge>
            {rentalAmount > 0 && (
              <Badge
                className={`border-0 ${
                  rentalStatus === "paid"
                    ? "bg-[#4A7C59]/15 text-[#4A7C59] hover:bg-[#4A7C59]/15"
                    : rentalStatus === "pending"
                    ? "bg-[#E89B33]/15 text-[#9C6512] hover:bg-[#E89B33]/15"
                    : "bg-[#EAE7E0] text-[#5C5C5C] hover:bg-[#EAE7E0]"
                }`}
                data-testid="detail-rental-badge"
              >
                Alquiler: {RENTAL_STATUS_LABELS[rentalStatus] || rentalStatus}
              </Badge>
            )}
          </div>
        </div>

        <ReservationProgress reservation={r} />

        <div className="grid lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
            <div className="text-overline mb-2">Recogida</div>
            <div className="font-display text-lg font-semibold text-[#1A1A1A]">{fecha(r.pickup_date)}</div>
            <div className="text-sm text-[#5C5C5C]">{r.pickup_time} · {r.pickup_location}</div>
          </div>
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
            <div className="text-overline mb-2">Devolución</div>
            <div className="font-display text-lg font-semibold text-[#1A1A1A]">{fecha(r.return_date)}</div>
            <div className="text-sm text-[#5C5C5C]">{r.return_time} · {r.return_location}</div>
          </div>
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
            <div className="text-overline mb-2">Importe total</div>
            {/* Alquiler más los extras elegidos: total_amount es solo el alquiler. */}
            <div className="font-display text-lg font-semibold text-[#1A1A1A]" data-testid="detail-total">
              {eur(importeTotal(r))}
            </div>
            {Number(r.extras_amount || 0) > 0 && (
              <div className="text-xs text-[#5C5C5C]">Alquiler {eur(rentalAmount)} · extras {eur(r.extras_amount)}</div>
            )}
            <div className="text-sm text-[#5C5C5C]">{r.km_included_per_day} km/día incluidos</div>
            {r.signal_payment_status === "paid" && (
              <div className="text-xs text-[#4A7C59] mt-2 font-medium" data-testid="detail-signal-paid">
                Señal cobrada: {eur(r.signal_amount)}
                {r.signal_paid_at ? ` · ${new Date(r.signal_paid_at).toLocaleDateString("es-ES")}` : ""}
                {rentalStatus === "pending" ? ` · resto ${eur(restoTrasSenal(r))} antes de la entrega` : ""}
              </div>
            )}
            {r.signal_payment_status === "pending" && Number(r.signal_amount || 0) > 0 && (
              <div className="text-xs text-[#9C6512] mt-2" data-testid="detail-signal-pending">
                Señal pendiente: {eur(r.signal_amount)} ({Number(r.signal_percent || 0).toFixed(0)} %)
              </div>
            )}
            {rentalStatus === "paid" && rentalAmount > 0 && (
              <div className="text-xs text-[#4A7C59] mt-2 font-medium">
                Alquiler cobrado{r.rental_paid_at ? ` · ${new Date(r.rental_paid_at).toLocaleDateString("es-ES")}` : ""}
              </div>
            )}
            {rentalChargeable && hasSavedCard && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={loading} variant="outline" size="sm" className="mt-2 border-marca text-marca" data-testid="detail-charge-rental-btn">
                    <Wallet className="h-3.5 w-3.5 mr-2" />
                    Cobrar alquiler
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      ¿Cobrar {eur(rentalDue)} del alquiler?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Se hará el cargo en la tarjeta guardada de la fianza, sin
                      intervención del cliente. Recibirá un recibo por email y
                      el movimiento quedará registrado en finanzas.
                      {rentalStatus === "external" &&
                        " Esta reserva está marcada como cobrada fuera de la plataforma: confirma que el alquiler NO se ha cobrado ya por otra vía."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onChargeRental} className="bg-marca hover:bg-marca-hover" data-testid="detail-charge-rental-confirm">
                      Cobrar {eur(rentalDue)}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {(rentalStatus === "pending" || r.payment_status === "pending") && (
              <Button
                disabled={loading}
                variant="outline"
                size="sm"
                className="mt-2 border-[#DCD8D0] text-[#1A1A1A]"
                onClick={onSendBalanceEmail}
                data-testid="detail-send-balance-btn"
              >
                Enviar aviso de pago al cliente
              </Button>
            )}
            {rentalStatus === "pending" && !hasSavedCard && (
              <div className="text-xs text-[#9C6512] mt-2">
                Alquiler pendiente · sin tarjeta guardada. El cliente puede
                pagarlo desde su enlace de reserva.
              </div>
            )}
            {rentalStatus === "external" && !hasSavedCard && (
              <div className="text-xs text-[#8A8A8A] mt-2">
                Alquiler cobrado fuera de la plataforma.
              </div>
            )}
          </div>
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
            <div className="text-overline mb-2">Fianza</div>
            <div className="font-display text-lg font-semibold text-[#1A1A1A]">{r.deposit_amount} €</div>
            {admin && (r.payment_status === "paid" || r.payment_status === "on_hold") && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={loading} variant="outline" size="sm" className="mt-2 border-marca text-marca" data-testid="detail-refund-btn">
                    <RefreshCcw className="h-3.5 w-3.5 mr-2" />
                    {r.payment_status === "on_hold" ? "Liberar fianza" : "Devolver fianza"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {r.payment_status === "on_hold"
                        ? `¿Liberar la preautorización de ${r.deposit_amount} €?`
                        : `¿Devolver ${r.deposit_amount} € al cliente?`}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {r.payment_status === "on_hold"
                        ? "La retención de la tarjeta se cancela y los fondos quedan disponibles para el cliente en horas. No hay comisión de Stripe."
                        : "Se ejecutará el refund en Stripe. El cliente lo recibirá en su cuenta en 5-10 días. Esta acción no se puede deshacer."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onRefund} className="bg-marca hover:bg-marca-hover" data-testid="detail-refund-confirm">
                      {r.payment_status === "on_hold" ? "Liberar fianza" : "Devolver fianza"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {r.payment_status === "released" && (
              <div className="text-xs text-[#4A7C59] mt-2 font-medium">Fianza liberada</div>
            )}
            {r.payment_status === "refunded" && (
              <div className="text-xs text-[#4A7C59] mt-2 font-medium">Fianza devuelta</div>
            )}
            {r.payment_status === "pending" && (
              <div className="text-xs text-[#8A8A8A] mt-2 flex items-center gap-1">
                <Wallet className="h-3.5 w-3.5" /> Pendiente de pago por el cliente
              </div>
            )}
          </div>
        </div>

        <RegistryPanel reservation={r} onChanged={load} admin={admin} />
        {admin && <PrivacyActions reservation={r} onChanged={load} />}

        <div className="bg-white border border-[#DCD8D0] rounded-xl p-5 mb-6" data-testid="detail-selfservice-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-marca" />
              <div>
                <div className="font-display font-semibold text-[#1A1A1A]">Recogida y devolución autónomas</div>
                <div className="text-xs text-[#8A8A8A]">
                  El cliente lo hace todo desde su móvil junto a la furgoneta; tú
                  solo abres y cierras con {brand?.remote_app_name || "la app del vehículo"} cuando te llegue el aviso.
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={Boolean(r.self_service)}
                onCheckedChange={(v) => onToggleSelfService(Boolean(v))}
                data-testid="detail-selfservice-toggle"
              />
              <span>{r.self_service ? "Activada" : "Desactivada"}</span>
            </label>
          </div>

          {(r.self_pickup || r.self_return) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {r.self_pickup && (
                <div className="bg-[#F7F5F0] border border-[#DCD8D0] rounded-lg p-4" data-testid="detail-selfpickup-state">
                  <div className="text-overline mb-1">Recogida</div>
                  <div className="text-sm text-[#1A1A1A] font-medium mb-1">
                    {r.self_pickup.status === "unlock_requested" && `🔓 Apertura solicitada — abre con ${brand?.remote_app_name || "la app del vehículo"}`}
                    {r.self_pickup.status === "unlocked" && "Abierta — cliente registrando el interior"}
                    {r.self_pickup.status === "completed" && "✅ Completada"}
                  </div>
                  {r.self_pickup.gps && (
                    <div className="text-xs text-[#5C5C5C]">
                      Cliente a {Math.round(r.self_pickup.gps.distance_m)} m del punto
                      (GPS ±{Math.round(r.self_pickup.gps.accuracy)} m) ·{" "}
                      {(r.self_pickup.photos_before || []).length} fotos previas
                    </div>
                  )}
                  {r.self_pickup.status === "unlock_requested" && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmSelfService("unlock")}
                      className="mt-3 bg-marca hover:bg-marca-hover text-white"
                      data-testid="detail-confirm-unlock-btn"
                    >
                      <Unlock className="h-3.5 w-3.5 mr-2" /> He abierto la furgoneta
                    </Button>
                  )}
                </div>
              )}
              {r.self_return && (
                <div className="bg-[#F7F5F0] border border-[#DCD8D0] rounded-lg p-4" data-testid="detail-selfreturn-state">
                  <div className="text-overline mb-1">Devolución</div>
                  <div className="text-sm text-[#1A1A1A] font-medium mb-1">
                    {r.self_return.status === "lock_requested" && `🔒 Pendiente de cierre — ciérralo con ${brand?.remote_app_name || "la app del vehículo"}`}
                    {r.self_return.status === "locked" && "✅ Cerrada"}
                  </div>
                  {r.self_return.gps && (
                    <div className="text-xs text-[#5C5C5C]">
                      Devuelta a {Math.round(r.self_return.gps.distance_m)} m del punto
                    </div>
                  )}
                  {r.self_return.status === "lock_requested" && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmSelfService("lock")}
                      className="mt-3 bg-marca hover:bg-marca-hover text-white"
                      data-testid="detail-confirm-lock-btn"
                    >
                      <Lock className="h-3.5 w-3.5 mr-2" /> He cerrado la furgoneta
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {(r.return_notices?.length > 0 || r.return_checklist) && (
          <div className="mb-6 space-y-3" data-testid="detail-returnprep">
            {(r.return_notices || []).map((n) => (
              <div key={n.id} className="bg-acento/10 border border-acento/30 rounded-xl px-4 py-3 text-sm text-[#7A3A28] flex flex-wrap justify-between gap-2">
                <span>
                  <strong>
                    {n.kind === "delay" ? "⏰ Aviso de retraso" : n.kind === "extension" ? "📅 Solicita ampliar el alquiler" : n.kind === "incident" ? "🔧 Incidencia o daño comunicado" : "💬 Aviso del viajero"}
                  </strong>
                  {n.estimated_time ? ` · llegada estimada ${n.estimated_time}` : ""}
                  {n.extra_days ? ` · +${n.extra_days} día${n.extra_days > 1 ? "s" : ""}` : ""}
                  {n.message ? ` — ${n.message}` : ""}
                </span>
                <span className="text-xs text-[#9C6512]">{new Date(n.created_at).toLocaleString("es-ES")}</span>
              </div>
            ))}
            {r.return_checklist && (
              <div className="bg-white border border-[#DCD8D0] rounded-xl px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2">
                <span>
                  <strong>Checklist de devolución del cliente:</strong>{" "}
                  {r.return_checklist.completed_at
                    ? `completada el ${new Date(r.return_checklist.completed_at).toLocaleString("es-ES")}`
                    : `${Object.values(r.return_checklist.checked || {}).filter(Boolean).length} tareas marcadas (en curso)`}
                  {r.return_checklist.notes ? ` · Notas: “${r.return_checklist.notes}”` : ""}
                </span>
              </div>
            )}
          </div>
        )}
        {!co && r.handover_data && (
          <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-[#8A8A8A]">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/reservations/${r.id}/return-prep/test-email`);
                  toast.success(`Email de preparación enviado como PRUEBA a ${data.sent_to}`);
                } catch (err) {
                  toast.error(formatError(err));
                }
              }}
              data-testid="detail-returnprep-test-email"
            >
              Enviar email de preparación (prueba a tu buzón)
            </Button>
            <span>Solo va al buzón del propietario; el cliente no recibe nada.</span>
          </div>
        )}
        <Tabs defaultValue="checkin">
          <TabsList className="bg-[#EAE7E0]">
            <TabsTrigger value="checkin" data-testid="tab-checkin">Pre-Checkin</TabsTrigger>
            <TabsTrigger value="handover" data-testid="tab-handover">Entrega</TabsTrigger>
            <TabsTrigger value="checkout" data-testid="tab-checkout">Devolución</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Gastos</TabsTrigger>
            <TabsTrigger value="settlement" data-testid="tab-settlement">Liquidación</TabsTrigger>
            <TabsTrigger value="contract" data-testid="tab-contract">Contrato</TabsTrigger>
          </TabsList>

          <TabsContent value="checkin" className="mt-4">
            {!ci ? (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]">
                El cliente aún no ha realizado el pre-checkin.
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Datos del viajero</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nombre">{ci.full_name}</Field>
                    <Field label="DNI / NIE">{ci.dni_number}</Field>
                    <Field label="Carné de conducir">{ci.license_number}</Field>
                    <Field label="Carné obtenido el">{fecha(ci.license_issue_date)}</Field>
                    <Field label="Fecha nacimiento">{fecha(ci.birth_date)}</Field>
                    <Field label="Dirección">{ci.address}</Field>
                    <Field label="Teléfono">{ci.phone || r.client_phone}</Field>
                    <Field label="Email">{ci.email || r.client_email}</Field>
                    <Field label="Países a visitar">{ci.countries_to_visit || "—"}</Field>
                  </div>
                  {(ci.dni_photo_front || ci.dni_photo_back || ci.license_photo) && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-5">
                      {ci.dni_photo_front && <a href={ci.dni_photo_front} target="_blank" rel="noreferrer"><img src={ci.dni_photo_front} alt="DNI delante" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                      {ci.dni_photo_back && <a href={ci.dni_photo_back} target="_blank" rel="noreferrer"><img src={ci.dni_photo_back} alt="DNI detrás" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                      {ci.license_photo && <a href={ci.license_photo} target="_blank" rel="noreferrer"><img src={ci.license_photo} alt="Carné" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                    </div>
                  )}
                </div>

                {ci.additional_driver && (
                  <div className="bg-white border border-[#DCD8D0] rounded-xl p-6" data-testid="detail-additional-driver">
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">
                      Segundo conductor <span className="text-xs font-normal text-[#4A7C59]">(sin coste)</span>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Nombre">{ci.additional_driver.full_name}</Field>
                      <Field label="DNI / NIE">{ci.additional_driver.dni_number}</Field>
                      <Field label="Carné de conducir">{ci.additional_driver.license_number}</Field>
                      <Field label="Carné obtenido el">{fecha(ci.additional_driver.license_issue_date)}</Field>
                      <Field label="Fecha nacimiento">{fecha(ci.additional_driver.birth_date)}</Field>
                    </div>
                    {(ci.additional_driver.dni_photo_front || ci.additional_driver.dni_photo_back || ci.additional_driver.license_photo) && (
                      <div className="grid sm:grid-cols-3 gap-3 mt-5">
                        {ci.additional_driver.dni_photo_front && <a href={ci.additional_driver.dni_photo_front} target="_blank" rel="noreferrer"><img src={ci.additional_driver.dni_photo_front} alt="DNI 2º conductor delante" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                        {ci.additional_driver.dni_photo_back && <a href={ci.additional_driver.dni_photo_back} target="_blank" rel="noreferrer"><img src={ci.additional_driver.dni_photo_back} alt="DNI 2º conductor detrás" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                        {ci.additional_driver.license_photo && <a href={ci.additional_driver.license_photo} target="_blank" rel="noreferrer"><img src={ci.additional_driver.license_photo} alt="Carné 2º conductor" className="w-full rounded-lg border border-[#DCD8D0]" /></a>}
                      </div>
                    )}
                  </div>
                )}

                {(r.selected_items || []).length > 0 && (
                  <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Mejoras y extras contratados</h3>
                    <div className="space-y-2">
                      {r.selected_items.map((it) => (
                        <div key={it.product_id} className="flex items-center justify-between text-sm bg-[#F7F5F0] px-4 py-2.5 rounded-lg">
                          <div>
                            <span className="text-[#1A1A1A] font-medium">{it.name}</span>
                            {it.qty > 1 && <span className="text-[#8A8A8A]"> ×{it.qty}</span>}
                            <span className="text-xs text-[#8A8A8A] ml-2">
                              ({it.price_type === "per_day" ? `${eur(it.unit_price)}/día` : eur(it.unit_price)})
                            </span>
                          </div>
                          <span className={`font-semibold ${it.subtotal > 0 ? "text-marca" : "text-[#8A8A8A]"}`}>
                            {it.subtotal > 0 ? eur(it.subtotal) : "Incluido"}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-sm px-4 pt-2 border-t border-[#DCD8D0]">
                        <span className="font-medium text-[#1A1A1A]">Total extras</span>
                        <div className="text-right">
                          <span className="font-display font-bold text-marca">{eur(r.extras_amount)}</span>
                          <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${r.extras_payment_status === "paid" ? "bg-[#4A7C59]/15 text-[#4A7C59]" : "bg-[#E89B33]/15 text-[#9C6512]"}`}>
                            {r.extras_payment_status === "paid" ? "Pagado" : "Pendiente"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {ci.signature && (
                  <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">Firma del viajero</h3>
                    <img src={ci.signature} alt="Firma" className="max-w-sm border border-[#DCD8D0] rounded-lg" />
                    <div className="text-xs text-[#8A8A8A] mt-2">Firmado el {new Date(ci.submitted_at).toLocaleString("es-ES")}</div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="expenses" className="mt-4">
            {!r.checkout_data ? (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]">
                El control de gastos estará disponible tras registrar la devolución del vehículo.
              </div>
            ) : (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-1">Control de gastos</h3>
                <p className="text-sm text-[#5C5C5C] mb-5">
                  Presupuesto de cargos de la devolución: reparaciones, km adicionales,
                  combustible y tasa de repostaje. Envíalo al viajero cuando esté completo;
                  al liquidar la fianza podrás importar estas líneas.
                </p>
                <ExpenseSheet reservation={r} onSaved={load} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="settlement" className="mt-4">
            {r.deposit_settled && r.settlement ? (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-6" data-testid="settlement-done">
                <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Fianza liquidada</h3>
                <div className="space-y-2 max-w-lg">
                  {(r.settlement.charges || []).map((c, i) => (
                    <div key={i} className="flex justify-between text-sm bg-[#F7F5F0] px-4 py-2.5 rounded-lg">
                      <span className="text-[#1A1A1A]">{c.concept}</span>
                      <span className="font-semibold text-[#B54434]">{eur(c.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm px-4 pt-2 border-t border-[#DCD8D0]">
                    <span className="font-medium">Total retenido</span>
                    <span className="font-display font-bold text-[#B54434]">
                      {eur(r.settlement.total_charges)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm px-4">
                    <span className="font-medium">Liberado / devuelto al cliente</span>
                    <span className="font-display font-bold text-[#4A7C59]">
                      {eur(Math.max(0, Number(r.settlement.deposit_amount || 0) - Number(r.settlement.total_charges || 0)))}
                    </span>
                  </div>
                  <div className="text-xs text-[#8A8A8A] px-4 pt-1">
                    Liquidada por {r.settlement.settled_by} · {new Date(r.settlement.settled_at).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
            ) : !r.handover_data ? (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]">
                La liquidación estará disponible tras registrar la entrega del vehículo.
              </div>
            ) : (
              <ReturnSettlement reservation={r} onSaved={load} />
            )}
          </TabsContent>

          <TabsContent value="handover" className="mt-4">
            {!ci ? (
              <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]">
                Espera a que el cliente complete el Pre-Checkin antes de la entrega.
              </div>
            ) : r.handover_data ? (
              <div className="space-y-6">
                <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Entrega registrada</h3>
                  <div className="grid sm:grid-cols-4 gap-4 mb-3">
                    <Field label="Km salida">{r.handover_data.km_start}</Field>
                    <Field label="Combustible">{nivelCombustible(r.handover_data.fuel_level)}</Field>
                    <Field label="Agua limpia">{r.handover_data.fresh_water_level}</Field>
                    <Field label="Neumáticos">{r.handover_data.tire_state || "—"}</Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <Field label="Aguas grises">{r.handover_data.grey_water_emptied ? "Vaciadas" : "NO vaciadas"}</Field>
                    <Field label="Aguas negras">{r.handover_data.black_water_emptied ? "Vaciadas" : "NO vaciadas"}</Field>
                  </div>
                  {r.handover_data.damage_notes && (
                    <Field label="Daños previos">{r.handover_data.damage_notes}</Field>
                  )}
                  {r.handover_data.additional_driver && (
                    <div className="mt-4 pt-4 border-t border-[#DCD8D0]">
                      <div className="text-overline mb-2">Conductor adicional</div>
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <Field label="Nombre">{r.handover_data.additional_driver.full_name}</Field>
                        <Field label="DNI">{r.handover_data.additional_driver.dni_number}</Field>
                        <Field label="Carné">{r.handover_data.additional_driver.license_number}</Field>
                        <Field label="Obtenido">{fecha(r.handover_data.additional_driver.license_issue_date)}</Field>
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    <PhotoGrid photos={r.handover_data.photos} />
                  </div>
                  {r.handover_data.damage_photos?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-overline mb-2">Fotos daños previos</div>
                      <PhotoGrid photos={r.handover_data.damage_photos} />
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {r.handover_data.traveler_signature && (
                    <div className="bg-white border border-[#DCD8D0] rounded-xl p-4">
                      <div className="text-overline mb-2">Firma viajero</div>
                      <img src={r.handover_data.traveler_signature} alt="firma viajero" className="max-w-full border border-[#DCD8D0] rounded-lg" />
                    </div>
                  )}
                  {r.handover_data.owner_signature && (
                    <div className="bg-white border border-[#DCD8D0] rounded-xl p-4">
                      <div className="text-overline mb-2">Firma propietario</div>
                      <img src={r.handover_data.owner_signature} alt="firma propietario" className="max-w-full border border-[#DCD8D0] rounded-lg" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <HandoverForm reservation={r} onSaved={load} />
            )}
          </TabsContent>

          <TabsContent value="checkout" className="mt-4">
            {!co ? (
              !r.handover_data ? (
                <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]">
                  La devolución estará disponible tras registrar la entrega del vehículo.
                </div>
              ) : (
                <AdminCheckoutForm reservation={r} onSaved={load} />
              )
            ) : (
              <div className="space-y-6">
                <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Estado del vehículo · Regreso</h3>
                  <div className="grid sm:grid-cols-4 gap-4 mb-3">
                    <Field label="Km llegada">{co.km_end}</Field>
                    <Field label="Km recorridos">{co.km_total ?? "—"}</Field>
                    <Field label="Km extra">{co.km_extra ?? 0}</Field>
                    <Field label="Combustible">{nivelCombustible(co.fuel_level)}</Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <Field label="Limp. interior">{co.interior_clean ? "OK" : "Insatisfactorio"}</Field>
                    <Field label="Limp. exterior">{co.exterior_clean ? "OK" : "Insatisfactorio"}</Field>
                    <Field label="Devuelto a tiempo">{co.returned_on_time ? "Sí" : "No"}</Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <Field label="Aguas negras vaciadas">{co.black_water_emptied ? "Sí" : "No"}</Field>
                    <Field label="Aguas grises vaciadas">{co.grey_water_emptied ? "Sí" : "No"}</Field>
                    <Field label="Depósito agua llenado">{co.fresh_water_filled ? "Sí" : "No"}</Field>
                  </div>
                  {co.extra_charge > 0 && (
                    <div className="bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-lg p-3 text-sm text-[#9C6512] mb-4">
                      Cargo por exceso de km: <strong>{eur(co.extra_charge)}</strong>
                    </div>
                  )}
                  {co.incidents && <Field label="Incidencias">{co.incidents}</Field>}
                  {co.damages && <div className="mt-3"><Field label="Daños">{co.damages}</Field></div>}
                  <div className="mt-4">
                    <PhotoGrid photos={co.photos} />
                  </div>
                </div>

                {co.signature && (
                  <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">Firma de devolución</h3>
                    <img src={co.signature} alt="Firma" className="max-w-sm border border-[#DCD8D0] rounded-lg" />
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contract" className="mt-4">
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-8">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <h3 className="font-display text-xl font-bold text-[#1A1A1A]">Contrato de alquiler</h3>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={onSendContract}
                    disabled={!ci}
                    variant="outline"
                    size="sm"
                    className="border-marca text-marca"
                    data-testid="detail-contract-send-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Enviar por email
                  </Button>
                  <Button
                    onClick={onDownloadContract}
                    disabled={!ci}
                    className="bg-marca hover:bg-marca-hover text-white"
                    size="sm"
                    data-testid="detail-contract-pdf-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    {ci ? "Descargar PDF" : "Tras Pre-Checkin"}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-[#5C5C5C] leading-relaxed mb-4">
                Conforme al contrato: el viajero examina el estado del
                vehículo ({r.vehicle?.name || "sin vehículo"}{r.vehicle?.plate ? ` · ${r.vehicle.plate}` : ""}),
                verifica el inventario y se compromete a devolverlo en su
                estado original a la hora y lugar convenidos.
              </p>
              <ul className="text-sm text-[#5C5C5C] space-y-1 list-disc pl-5">
                <li>Importe total alquiler: {eur(r.total_amount)}</li>
                <li>Fianza: {r.deposit_amount} € · cobrada digitalmente</li>
                <li>Km incluidos: {r.km_included_per_day} / día · extra a {eur(r.extra_km_rate)}/km</li>
                <li>Limpieza interior y exterior obligatoria entre alquileres</li>
                <li>Vaciado obligatorio de aguas negras y grises antes de la devolución</li>
              </ul>
              {r.notes && (
                <div className="mt-6 pt-4 border-t border-[#DCD8D0]">
                  <div className="text-overline mb-1">Notas internas</div>
                  <p className="text-sm text-[#5C5C5C]">{r.notes}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
